
import React, { useState, useEffect, useMemo } from 'react';
import type { GoogleGenAI } from "@google/genai";
import { useAuth } from './hooks/useAuth';
import { useGeminiAI } from './hooks/useGeminiAI';
import { useAppConfig } from './hooks/useAppConfig';
import { LoginView } from './views/auth/LoginView';
import { SplashScreen } from './components/SplashScreen';
import { roleViewComponentMap, DashboardViewProps } from './config/routeMappings';
import { NoRoleHomeView } from './views/user/NoRoleHomeView';
import { UserRole, AppUser, UserStatus } from './types';
import { OnboardingView } from './views/broker/OnboardingView';
import { ZohoOnboardingView } from './views/broker/ZohoOnboardingView';
import { ExclamationTriangleIcon } from './components/Icons';
import { useConnection } from './context/ConnectionContext';
import { SharedPropertyView } from './views/public/SharedPropertyView';
import { SharedGroupView } from './views/public/SharedGroupView'; // New
import { NewlyAddedView } from './views/public/NewlyAddedView'; // New
import { PublicReferralView } from './views/public/PublicReferralView'; // New
import { SalesProcessView } from './views/broker/SalesProcessView'; // New
import { SharedLocationView } from './views/public/SharedLocationView'; // New
import { ClientRegistrationView } from './views/public/ClientRegistrationView';
import { EarthquakeLandingView } from './views/public/EarthquakeLandingView';
import { usePageMetadata } from './hooks/usePageMetadata';
import { getClientSession } from './utils/clientSession';
import { decodeSelectionToCodes } from './utils/selectionDecoder';
import { parseAppRoute } from './utils/appRouterHelper';
import { sanitizeClientSessionState } from './utils/clientSessionSanitizer';
import pako from 'pako';
import { shortCodeMap } from './firebase/firestoreCollections';


const ConnectionStatusBanner: React.FC<{ isOffline: boolean }> = ({ isOffline }) => {
  if (!isOffline) return null;
  return (
    <div 
      className="fixed top-0 left-0 right-0 bg-yellow-500 text-white text-center p-2 z-[9999] flex items-center justify-center shadow-lg"
      role="alert"
      aria-live="assertive"
    >
      <ExclamationTriangleIcon className="w-5 h-5 mr-3" />
      <span className="font-semibold">Sin conexión.</span>
      <span className="hidden sm:inline ml-1"> Intentando reconectar... Algunas funciones pueden no estar disponibles.</span>
    </div>
  );
};

const App: React.FC = () => {
  const { isOffline } = useConnection();

  const [publicViewParams, setPublicViewParams] = useState<{propertyId: string; collectionName: string} | null>(null);
  const [groupViewParams, setGroupViewParams] = useState<{groupId: string} | null>(null);
  const [newlyAddedDate, setNewlyAddedDate] = useState<string | null>(null);
  const [isClientAccessMode, setIsClientAccessMode] = useState(false);
  const [isClientRegisterMode, setIsClientRegisterMode] = useState(false);
  const [isEarthquakeLandingMode, setIsEarthquakeLandingMode] = useState(false);
  const [isPublicReferralMode, setIsPublicReferralMode] = useState(false);
  const [isSalesProcessMode, setIsSalesProcessMode] = useState(false);
  const [locationViewParams, setLocationViewParams] = useState<{ city: string; locality: string } | null>(null);
  const [selectionCodes, setSelectionCodes] = useState<string | null>(null);
  const [newlyAddedRange, setNewlyAddedRange] = useState<{ startDate: Date; endDate: Date } | null>(null);
  const [forceOnlyRecent, setForceOnlyRecent] = useState(false);

  // Dynamic document title and meta description for /accesocliente route
  usePageMetadata(
    {
      title: 'Ciencuadras: Alivio hipotecario al 8%',
      description: 'Encuentra el alivio financiero para tu hogar con las opciones especiales de crédito y tasas del ocho por ciento disponibles',
      ogTitle: 'Ciencuadras: Alivio hipotecario al 8%',
      ogDescription: 'Encuentra el alivio financiero para tu hogar con las opciones especiales de crédito y tasas del ocho por ciento disponibles',
    },
    isClientAccessMode
  );

  useEffect(() => {
    const handleRouteChange = () => {
      const parsed = parseAppRoute();
      sanitizeClientSessionState(parsed.isClientAccessMode);
      setPublicViewParams(parsed.publicViewParams || null);
      setGroupViewParams(parsed.groupViewParams || null);
      setNewlyAddedDate(parsed.newlyAddedDate || null);
      setNewlyAddedRange(parsed.newlyAddedRange || null);
      setIsClientAccessMode(parsed.isClientAccessMode);
      setIsClientRegisterMode(parsed.isClientRegisterMode);
      setIsEarthquakeLandingMode(parsed.isEarthquakeLandingMode);
      setIsPublicReferralMode(parsed.isPublicReferralMode);
      setIsSalesProcessMode(parsed.isSalesProcessMode);
      setLocationViewParams(parsed.locationViewParams || null);
      setSelectionCodes(parsed.selectionCodes || null);
    };

    window.addEventListener('hashchange', handleRouteChange);
    window.addEventListener('popstate', handleRouteChange);
    handleRouteChange(); // Initial check

    return () => {
      window.removeEventListener('hashchange', handleRouteChange);
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);
  
  const { 
    firebaseUser, 
    appUser,
    loginError: authLoginError, 
    passwordResetMessage,
    authLoading, 
    appUserLoading,
    handleGoogleSignIn,
    handleEmailPasswordSignIn,
    handleEmailPasswordSignUp,
    handlePasswordReset,
    handleLogout,
    lastSignInMethod,
    isEnvironmentReady,
    handleCompleteOnboarding,
    updateAppUserProfile,
  } = useAuth();

  useEffect(() => {
    if (!authLoading && !appUserLoading) {
      if (firebaseUser && appUser) {
        if (newlyAddedDate || localStorage.getItem('redirect_only_recent') === 'true') {
          if (updateAppUserProfile) {
            updateAppUserProfile({
              revision_link_nuevos: new Date().toISOString()
            }).catch(err => console.error("Error actualizando revision_link_nuevos:", err));
          }
          localStorage.removeItem('redirect_only_recent');
          setForceOnlyRecent(true);
          setNewlyAddedDate(null);
          window.location.hash = '';
        }
      }
    }
  }, [firebaseUser, appUser, authLoading, appUserLoading, newlyAddedDate, updateAppUserProfile]);

  const { appConfig, configLoading, configError, isFeatureEnabled, getAppVersion } = useAppConfig();
  
  const { ai, aiInitializationError } = useGeminiAI();

  const guestClientUser: AppUser | null = useMemo(() => {
    if (isClientAccessMode) {
      const session = getClientSession();
      return {
        id: session?.clientId || 'guest-client',
        uid: session?.clientId || 'guest-client',
        email: null,
        displayName: session?.nombre || 'Cliente',
        username: session?.nombre || 'cliente',
        photoURL: null,
        role: UserRole.CLIENTE,
        status: UserStatus.ACTIVE,
        createdAt: { seconds: Math.floor(Date.now() / 1000), nanoseconds: 0 } as any,
        lastLoginAt: { seconds: Math.floor(Date.now() / 1000), nanoseconds: 0 } as any,
        onboardingCompleted: true,
      };
    }
    return null;
  }, [isClientAccessMode]);

  const effectiveAppUser = guestClientUser || appUser;

  const renderAppContent = () => {
    if (isEarthquakeLandingMode) {
      return (
        <EarthquakeLandingView 
          onGoToRegistration={() => {
            window.location.hash = '';
            window.history.pushState(null, '', '/accesocliente');
            window.dispatchEvent(new Event('popstate'));
          }}
        />
      );
    }

    if (isClientRegisterMode) {
      return <ClientRegistrationView />;
    }

    if (configLoading || !isEnvironmentReady || (!isClientAccessMode && !isPublicReferralMode && !isSalesProcessMode && !newlyAddedDate && !isEarthquakeLandingMode && (authLoading || appUserLoading))) {
      return <SplashScreen 
                isEnvironmentReady={isEnvironmentReady}
                configLoading={configLoading}
                firebaseAuthLoading={authLoading}
                appUserProfileLoading={appUserLoading}
                error={configError || aiInitializationError}
             />;
    }
    
    if (publicViewParams) {
        return <SharedPropertyView {...publicViewParams} />;
    }
    
    if (groupViewParams) {
        return <SharedGroupView {...groupViewParams} />;
    }

    if (newlyAddedDate) {
        return (
          <NewlyAddedView 
            dateStr={newlyAddedDate} 
            onLoginRedirect={() => {
              localStorage.setItem('redirect_only_recent', 'true');
              setNewlyAddedDate(null);
              window.location.hash = '';
            }}
          />
        );
    }

    if (isPublicReferralMode) {
        return <PublicReferralView />;
    }

    if (isSalesProcessMode) {
        return <SalesProcessView />;
    }

    if (locationViewParams) {
        return <SharedLocationView city={locationViewParams.city} locality={locationViewParams.locality} />;
    }

    if (isClientAccessMode) {
        if (guestClientUser) {
            const viewProps: DashboardViewProps = {
                onLogout: () => {
                  window.history.replaceState(null, '', '/');
                  setIsClientAccessMode(false);
                  setIsClientRegisterMode(false);
                  window.dispatchEvent(new Event('popstate'));
                },
                firebaseUser: null,
                appUser: guestClientUser,
                ai: ai,
            };
            const ViewComponent = roleViewComponentMap[UserRole.CLIENTE];
            return <ViewComponent {...viewProps} />;
        }
        return (
            <div className="min-h-screen flex items-center justify-center">
              <p>Error al entrar en modo cliente.</p>
            </div>
        );
    }

    // Standard authentication flow
    const combinedLoginError = authLoginError || configError; 

    if (firebaseUser && appUser) {
      const isBrokerType = appUser.role === UserRole.BROKER || appUser.role === UserRole.BROKER_INTERNO || appUser.role === UserRole.BROKER_PREMIUM;
      if (isBrokerType && !appUser.onboardingCompleted) {
          return (
              <OnboardingView 
                  appUser={appUser}
                  onCompleteOnboarding={handleCompleteOnboarding}
                  onLogout={handleLogout}
              />
          );
      }

      if (isBrokerType && !appUser.zohoOnboardingCompleted) {
          return (
              <ZohoOnboardingView 
                  appUser={appUser}
                  onCompleteOnboarding={async (userId, data) => {
                      if (updateAppUserProfile) {
                          await updateAppUserProfile(userId, {
                              ...data,
                              zohoOnboardingCompleted: true
                          });
                      }
                  }}
                  onLogout={handleLogout}
              />
          );
      }
      
      const ViewComponent = roleViewComponentMap[appUser.role] || NoRoleHomeView;
      const viewProps: DashboardViewProps = {
        onLogout: handleLogout,
        firebaseUser: firebaseUser,
        appUser: appUser,
        ai: ai,
        preselectedCodes: selectionCodes || undefined,
        newlyAddedRange: newlyAddedRange || undefined,
        forceOnlyRecent: forceOnlyRecent || undefined,
        onConsumeForceOnlyRecent: () => setForceOnlyRecent(false),
      };

      return <ViewComponent {...viewProps} />;
    } else {
      return <LoginView 
                onGoogleSignIn={handleGoogleSignIn}
                onEmailPasswordSignIn={handleEmailPasswordSignIn}
                onEmailPasswordSignUp={handleEmailPasswordSignUp}
                onPasswordReset={handlePasswordReset}
                loginError={combinedLoginError}
                passwordResetMessage={passwordResetMessage}
                aiInitializationError={aiInitializationError}
                isGoogleLoading={authLoading && lastSignInMethod === 'google'}
                isEmailLoading={authLoading && lastSignInMethod === 'email'}
                appVersion={getAppVersion()}
                isGoogleLoginEnabled={isFeatureEnabled('login_google_enabled')}
                isEmailPasswordLoginEnabled={isFeatureEnabled('login_email_password_enabled')}
             />; 
    }
  };

  return (
    <>
      <ConnectionStatusBanner isOffline={isOffline} />
      {renderAppContent()}
    </>
  );
};

export default App;
